import { MessageSchema } from "@redotech/redo-model/agents/llm-messages";
import { PageContextSchema } from "@redotech/redo-model/agents/page-context";
import { unaryStreamRpc } from "@redotech/rpc/definition";
import * as z from "zod";

/**
 * Streaming event schemas for the Ask Redo agent.
 *
 * These map 1:1 to Vercel AI SDK `UIMessageChunk` types on the client
 * via `AskRedoRpcTransport`, which translates them into the wire
 * format `useChat` consumes. See:
 *   redo/merchant/app/src/app/ask-redo/transport/ask-redo-rpc-transport.ts
 *
 * The client never consumes these events directly — the transport
 * adapter converts them to `UIMessageChunk`s.
 */

const TextDeltaEventSchema_ = z.object({
  type: z.literal("text-delta"),
  textDelta: z.string(),
});
export const AskRedoTextDeltaEventSchema: z.ZodType<AskRedoTextDeltaEventSchema> =
  TextDeltaEventSchema_;
export interface AskRedoTextDeltaEventSchema extends z.infer<
  typeof TextDeltaEventSchema_
> {}

const ToolInputStartEventSchema_ = z.object({
  type: z.literal("tool-input-start"),
  toolCallId: z.string(),
  toolName: z.string(),
});
export const AskRedoToolInputStartEventSchema: z.ZodType<AskRedoToolInputStartEventSchema> =
  ToolInputStartEventSchema_;
export interface AskRedoToolInputStartEventSchema extends z.infer<
  typeof ToolInputStartEventSchema_
> {}

const ToolInputDeltaEventSchema_ = z.object({
  type: z.literal("tool-input-delta"),
  toolCallId: z.string(),
  inputTextDelta: z.string(),
});
export const AskRedoToolInputDeltaEventSchema: z.ZodType<AskRedoToolInputDeltaEventSchema> =
  ToolInputDeltaEventSchema_;
export interface AskRedoToolInputDeltaEventSchema extends z.infer<
  typeof ToolInputDeltaEventSchema_
> {}

const ToolCallEventSchema_ = z.object({
  type: z.literal("tool-call"),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.unknown(),
});
export const AskRedoToolCallEventSchema: z.ZodType<AskRedoToolCallEventSchema> =
  ToolCallEventSchema_;
export interface AskRedoToolCallEventSchema extends z.infer<
  typeof ToolCallEventSchema_
> {}

const ToolApprovalRequestEventSchema_ = z.object({
  type: z.literal("tool-approval-request"),
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.unknown(),
  prompt: z.string().optional(),
  confirmationData: z.unknown().optional(),
});
export const AskRedoToolApprovalRequestEventSchema: z.ZodType<AskRedoToolApprovalRequestEventSchema> =
  ToolApprovalRequestEventSchema_;
export interface AskRedoToolApprovalRequestEventSchema extends z.infer<
  typeof ToolApprovalRequestEventSchema_
> {}

const ToolOutputAvailableEventSchema_ = z.object({
  type: z.literal("tool-output-available"),
  toolCallId: z.string(),
  toolName: z.string(),
  output: z.unknown(),
});
export const AskRedoToolOutputAvailableEventSchema: z.ZodType<AskRedoToolOutputAvailableEventSchema> =
  ToolOutputAvailableEventSchema_;
export interface AskRedoToolOutputAvailableEventSchema extends z.infer<
  typeof ToolOutputAvailableEventSchema_
> {}

const ToolOutputErrorEventSchema_ = z.object({
  type: z.literal("tool-output-error"),
  toolCallId: z.string(),
  toolName: z.string(),
  errorText: z.string(),
});
export const AskRedoToolOutputErrorEventSchema: z.ZodType<AskRedoToolOutputErrorEventSchema> =
  ToolOutputErrorEventSchema_;
export interface AskRedoToolOutputErrorEventSchema extends z.infer<
  typeof ToolOutputErrorEventSchema_
> {}

const ToolOutputDeniedEventSchema_ = z.object({
  type: z.literal("tool-output-denied"),
  toolCallId: z.string(),
  toolName: z.string(),
});
export const AskRedoToolOutputDeniedEventSchema: z.ZodType<AskRedoToolOutputDeniedEventSchema> =
  ToolOutputDeniedEventSchema_;
export interface AskRedoToolOutputDeniedEventSchema extends z.infer<
  typeof ToolOutputDeniedEventSchema_
> {}

const ReasoningEventSchema_ = z.object({
  type: z.literal("reasoning"),
  text: z.string(),
});
export const AskRedoReasoningEventSchema: z.ZodType<AskRedoReasoningEventSchema> =
  ReasoningEventSchema_;
export interface AskRedoReasoningEventSchema extends z.infer<
  typeof ReasoningEventSchema_
> {}

const StatusEventSchema_ = z.object({
  type: z.literal("status"),
  status: z.string(),
});
export const AskRedoStatusEventSchema: z.ZodType<AskRedoStatusEventSchema> =
  StatusEventSchema_;
export interface AskRedoStatusEventSchema extends z.infer<
  typeof StatusEventSchema_
> {}

const EmergencyEscalationCreatedEventSchema_ = z.object({
  type: z.literal("emergency-escalation-created"),
  threadId: z.string(),
  escalation: z.object({
    escalationId: z.string(),
    intercomConversationId: z.string(),
    noticeTopic: z.string(),
    status: z.enum(["waiting", "human-active"]),
  }),
});
export const AskRedoEmergencyEscalationCreatedEventSchema: z.ZodType<AskRedoEmergencyEscalationCreatedEventSchema> =
  EmergencyEscalationCreatedEventSchema_;
export interface AskRedoEmergencyEscalationCreatedEventSchema extends z.infer<
  typeof EmergencyEscalationCreatedEventSchema_
> {}

const CompleteEventSchema_ = z.object({
  type: z.literal("complete"),
  threadId: z.string(),
  newMessages: z.array(MessageSchema),
});
export const AskRedoCompleteEventSchema: z.ZodType<AskRedoCompleteEventSchema> =
  CompleteEventSchema_;
export interface AskRedoCompleteEventSchema extends z.infer<
  typeof CompleteEventSchema_
> {}

const ErrorEventSchema_ = z.object({
  type: z.literal("error"),
  error: z.string(),
});
export const AskRedoErrorEventSchema: z.ZodType<AskRedoErrorEventSchema> =
  ErrorEventSchema_;
export interface AskRedoErrorEventSchema extends z.infer<
  typeof ErrorEventSchema_
> {}

const AskRedoStreamEventSchema_ = z.discriminatedUnion("type", [
  TextDeltaEventSchema_,
  ToolInputStartEventSchema_,
  ToolInputDeltaEventSchema_,
  ToolCallEventSchema_,
  ToolApprovalRequestEventSchema_,
  ToolOutputAvailableEventSchema_,
  ToolOutputErrorEventSchema_,
  ToolOutputDeniedEventSchema_,
  ReasoningEventSchema_,
  StatusEventSchema_,
  EmergencyEscalationCreatedEventSchema_,
  CompleteEventSchema_,
  ErrorEventSchema_,
]);
export const AskRedoStreamEventSchema: z.ZodType<AskRedoStreamEvent> =
  AskRedoStreamEventSchema_;
export type AskRedoStreamEvent = z.infer<typeof AskRedoStreamEventSchema_>;

/**
 * Approval response from the client. When a tool requires approval,
 * the server emits `tool-approval-request` and closes the stream.
 * The client renders the approval UI (AI Elements <Confirmation>),
 * the user clicks approve/deny, and the next `streamAskRedo` call
 * carries the response here so the server can resume the agent.
 */
const AskRedoApprovalResponseSchema_ = z.object({
  toolCallId: z.string(),
  approved: z.boolean(),
  reason: z.string().optional(),
});
export const AskRedoApprovalResponseSchema: z.ZodType<AskRedoApprovalResponseSchema> =
  AskRedoApprovalResponseSchema_;
export interface AskRedoApprovalResponseSchema extends z.infer<
  typeof AskRedoApprovalResponseSchema_
> {}

/**
 * Result of a browser-executed tool. When the agent calls a tool with no
 * server-side `execute` (a `ClientSideToolDefinition`), the tool call is
 * parked on the thread and the stream closes. The client executes the tool
 * (e.g. screenshots the coverage builder preview iframe), then the next
 * `streamAskRedo` call carries the result(s) here so the server can write
 * them to the thread and resume the agent.
 */
const AskRedoClientToolResultSchema_ = z.object({
  toolCallId: z.string(),
  output: z.json().meta({ annotation: "JSON" }),
  isError: z.boolean().optional(),
  errorMetadata: z.record(z.string(), z.json()).optional(),
});
export const AskRedoClientToolResultSchema: z.ZodType<AskRedoClientToolResultSchema> =
  AskRedoClientToolResultSchema_;
export interface AskRedoClientToolResultSchema extends z.infer<
  typeof AskRedoClientToolResultSchema_
> {}

export const streamAskRedo = unaryStreamRpc({
  name: "streamAskRedo" as const,
  input: z.object({
    messages: z.array(MessageSchema),
    threadId: z
      .string()
      .optional()
      .describe(
        "If left undefined, a new Ask Redo thread will be created. Otherwise the existing thread is continued.",
      ),
    userEmail: z
      .string()
      .optional()
      .describe("User email for context when creating a new thread."),
    pageContext: PageContextSchema.optional().describe(
      "Page context object with structured data (pageDescription, preferredSkill, pageData).",
    ),
    merchantTimezone: z
      .string()
      .optional()
      .describe(
        "The merchant's IANA timezone (e.g., 'America/Denver'). Used for date-aware responses.",
      ),
    approvalResponse: AskRedoApprovalResponseSchema.optional().describe(
      "If this turn is resuming after a tool approval, the user's decision is carried here so the server can flip the pending tool's confirmed flag and resume execution.",
    ),
    clientToolResults: z
      .array(AskRedoClientToolResultSchema)
      .optional()
      .describe(
        "If this turn is resuming after browser-executed tool calls, their results are carried here so the server can write them to the thread and resume the agent.",
      ),
    emergencyEscalation: z
      .boolean()
      .optional()
      .describe(
        "When true, bypasses Ask Redo and creates the same support escalation used by the human handoff flow without adding a user chat message.",
      ),
  }),
  output: AskRedoStreamEventSchema,
});
